import { action, makeObservable, observable } from "mobx";
import * as usersService from "../requests/users";
import * as authenticationService from "../requests/authentication";
import { RootStore } from "./RootStore";
import { setDatadogRumUser } from "../datadogRum";

export const STATUSES = [
  "fresh",
  "received_by_sales",
  "work_in_progress",
  "pending_decision",
  "contract_signed",
  "completed",
  "failed",
  "hibernated",
];

export const MFA_INITIALIZED_STATUS = "initialized";
export const MFA_SETUP_REQUIRED_HEADER = "x-required-mfa-modes";
export const MFA_URL = "/mfa_setup";
const MFA_URL_REGEX = new RegExp(`^${MFA_URL}/?$`);

export const LOGIN_SUCCESS_STATUS = "success";
export const LOGIN_ERROR_STATUS = "error";

export class AuthStore {
  @observable bannerStore;

  @observable context;

  @observable isLoading;

  @observable isAuthenticated;

  @observable mfaToken;

  @observable rootStore;

  constructor(bannerStore) {
    makeObservable(this);
    this.bannerStore = bannerStore;
    this.isLoading = true;
    this.isAuthenticated = true;
    this.context = {};
    this.rootStore = new RootStore(this);

    this.retrieveCurrentUserContext();
  }

  @action
  setContext = (context) => {
    context.statuses = STATUSES;

    this.context = context;

    const currentUser = context?.currentUser;

    if (currentUser) {
      setDatadogRumUser({
        id: currentUser.id,
        name: currentUser.fullName,
        email: currentUser.email,
      });
    }
  };

  @action
  setIsAuthenticated = (isAuthenticated) => {
    this.isAuthenticated = isAuthenticated;
  };

  @action
  setIsLoading = (isLoading) => {
    this.isLoading = isLoading;
  };

  @action
  setMfaToken = (mfaToken) => {
    this.mfaToken = mfaToken;
  }

  retrieveEmailIfRemembered = async () => {
    const user = await usersService.getEmail();
    return user.email;
  };

  retrieveCurrentUserContext = async () => {
    try {
      const { data, headers } = await usersService.getMe();
      this.setContext(data);
      
      const requiredMfaModes = headers[MFA_SETUP_REQUIRED_HEADER];

      if (requiredMfaModes && !this.context.currentUser.internalAccount) {
        const isAlreadyOnMfaSetupPage = MFA_URL_REGEX.test(window.location.pathname);

        if (!isAlreadyOnMfaSetupPage) {
          window.location.replace(`${MFA_URL}?mfa_modes=${requiredMfaModes}`);
        }
      } else {
        this.setIsAuthenticated(true);
      }
    } catch (err) {
      this.setIsAuthenticated(false);
    }

    this.setIsLoading(false);
  };

  resetPassword = async (userId) => {
    await authenticationService.resetPassword(userId);
  };

  changePassword = async (resetToken, password, passwordConfirmation) => {
    const { context } = await authenticationService.changePassword(
      resetToken,
      password,
      passwordConfirmation
    );

    this.setContext(context);
    this.setIsAuthenticated(true);
  };

  updateUserPassword = async (
    userId,
    currentPassword = "",
    password,
    passwordConfirmation,
    campaignId
  ) => {
    await authenticationService.updateUserPassword(
      userId,
      currentPassword,
      password,
      passwordConfirmation,
      campaignId
    );
  };

  logoutUser = async () => {
    try {
      await authenticationService.signOutUser();
    } catch (err) {
      console.log(err);
    } finally {
      this.setIsAuthenticated(false);
      this.setContext({});
      this.rootStore.clear();
    }
  };

  verifyMfaCodeForCurrentUser = async (mfaCode) => {
    const context = await authenticationService.postMfaVerify({
      mfa_code: mfaCode,
      token: this.mfaToken,
    });

    this.setContext(context);
    this.setIsAuthenticated(true);
    this.setIsLoading(false);
  }

  loginUser = async (payload) => {
    try {
      const response = await authenticationService.signInUser(payload);

      if (response.mfaStatus === MFA_INITIALIZED_STATUS) {
        this.setIsAuthenticated(true);
        this.setMfaToken(response.token);
        this.setContext({});
        this.setIsLoading(false);
        return MFA_INITIALIZED_STATUS;
      } else {
        this.setContext(response);
        this.setIsAuthenticated(true);
        this.setIsLoading(false);
        return LOGIN_SUCCESS_STATUS;
      }

    } catch (err) {
      this.setContext({});
      this.setIsLoading(false);
      this.setIsAuthenticated(false);

      return LOGIN_ERROR_STATUS;
    }
  };

  loginToOauth = async (payload) => {
    return await authenticationService.signInOauth(payload);
  };
}

export default AuthStore;
